// University of Massachusetts Boston: public college page (#2748), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UMass Boston publishes no current Common Data Set: its Office of
//    Institutional Research page lists editions only through 2023-2024. The
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator and the ADM2024 complete data file, unit 166638).
//    IPEDS gives no residency split and no early-round counts, so neither
//    appears here.
//  - UMass Boston Undergraduate Admissions: Apply as a First-Year Student
//    (deadlines, decision timing, requirements) and Test-Optional Admission
//    (FAQs). The deadline list carries no year; the admissions pages
//    advertise fall 2026 open houses, so it is read as the list for fall
//    2027 entry.
//  - UMass Boston Financial Aid, Cost of Attendance, "In-State Undergraduate
//    Dependent Student Full Time" table (12 semester hours) and the out-of-state
//    equivalent, living on campus. Quirk: the page states no academic year;
//    its tuition ($16,366 in-state, $39,586 out-of-state) matches the
//    Bursar's "AY 2026-2027 Rates" table, so the figures are taken as
//    2026-2027. The page's totals equal the sum of its items.
//  - College Scorecard, school id 166638 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=166638#admsns',
        read: '2026-10-01',
    },
    oir: {
        label: 'UMass Boston Office of Institutional Research, Common Data Set',
        url: 'https://www.umb.edu/oir/facts/common-data-set/',
        read: '2026-10-01',
    },
    apply: {
        label: 'UMass Boston Admissions, Apply as a First-Year Student',
        url: 'https://www.umb.edu/admissions/first-year-students/apply/',
        read: '2026-10-01',
    },
    tests: {
        label: 'UMass Boston Admissions, Test-Optional Admission',
        url: 'https://www.umb.edu/admissions/first-year-students/apply/test-optional-admission/',
        read: '2026-10-01',
    },
    costs: {
        label: 'UMass Boston Financial Aid, Cost of Attendance',
        url: 'https://www.umb.edu/financial-aid/apply-for-financial-aid/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?166638-University-of-Massachusetts-Boston',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-massachusetts-boston',
    kbId: 'university_of_massachusetts_boston',
    name: 'University of Massachusetts Boston',
    shortName: 'UMass Boston',
    aliases: ['UMass Boston', 'UMB'],
    location: 'Boston, MA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UMass Boston style guide); decoration only.
    branding: {
        primary: '#005A8B',
        secondary: '#FEDE42',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_massachusetts_boston_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 21262,
        admitted: 17813,
        enrolled: 2187,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UMass Boston publishes no first-year acceptance rate by college or major, and its last posted Common Data Set is the 2023-2024 edition, so these counts come from the federal IPEDS survey instead.',
        source: 'oir',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Every undergraduate program is open to test-optional applicants. One program has a recommended round:',
        rows: [
            { school: 'Nursing', requirement: 'UMass Boston recommends Early Action I (November 1) for Nursing applicants.', source: 'apply' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 15' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 1', decision: 'By January 1', binding: false },
            { plan: 'Early Action II', deadline: 'January 1', decision: 'By February 15', binding: false },
            { plan: 'Regular Decision', deadline: 'February 15', decision: null, binding: false },
            { plan: 'Rolling admission', deadline: 'July 15', decision: null, binding: false },
        ],
        note: 'Both Early Action rounds are non-binding, and every supporting document (transcript, essay and so on) must arrive by the deadline. Early Action brings priority for merit scholarships and financial aid and first access to on-campus housing. The application fee is $60. UMass Boston does not publish decision dates for Regular Decision or rolling admission.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'You may apply without SAT or ACT scores to any undergraduate program; the no-test option is offered for Early Action I, Early Action II and Regular Decision (the rolling July 15 date is not listed). Test-optional applicants stay eligible for merit scholarships and the Honors College. UMass Boston says the most competitive test-optional applicants have a recalculated GPA of 3.2 or higher with AP, honors or college preparatory courses. You may report your own scores on the application; enrolling students then send official scores.',
        source: 'tests',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [16366, 39586] },
            { item: 'Fees', amounts: [780, 780] },
            { item: 'Housing (on campus)', amounts: [13318, 13318] },
            { item: 'Food', amounts: [6915, 6915] },
            { item: 'Books', amounts: [1000, 1000] },
            { item: 'Transportation', amounts: [2400, 2400] },
            { item: 'Loan fees', amounts: [70, 70] },
            { item: 'Personal and miscellaneous', amounts: [1350, 1350] },
        ],
        totals: [42199, 65419],
        note: 'For a full-time (12 semester hours) dependent undergraduate living on campus, fall and spring. UMass Boston calls these estimates; the Bursar lists the actual charges.',
        source: 'costs',
        netPrice: {
            average: 22548,
            byIncome: [
                { income: '$0 to $30,000', amount: 22057 },
                { income: '$30,001 to $48,000', amount: 20837 },
                { income: '$48,001 to $75,000', amount: 27711 },
                { income: '$75,001 to $110,000', amount: 30258 },
                { income: 'Over $110,000', amount: 36294 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UMass Boston\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
